import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { BarChart3Icon, UserCheckIcon, UsersIcon, XIcon, CheckIcon, ChevronRightIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Select } from '../ui/Select';
import { Appointment } from '../../types';
import { getToday } from '../../utils/helpers';
import { exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { exportDashboardChartDataToExcel } from '../../utils/excelExport';
import { useAuth } from '../../contexts/AuthContext';
interface AppointmentsByUserProps {
  appointments: Appointment[];
}
export function DashboardAppointmentsByUser({
  appointments
}: AppointmentsByUserProps) {
  const {
    user
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const chartAxisColor = isDark ? '#9ca3af' : '#9ca3af';
  const chartTooltipBg = isDark ? '#1f2937' : '#ffffff';
  const chartTooltipBorder = isDark ? '1px solid #374151' : 'none';
  const chartTooltipColor = isDark ? '#f9fafb' : '#111827';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canDownloadPdf = user?.role === 'administrador' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [aptByUserGroupBy, setAptByUserGroupBy] = useState<'day' | 'month' | 'year'>('month');
  const [aptDateFrom, setAptDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [aptDateTo, setAptDateTo] = useState<string>(getToday());
  const [aptSelectedUsers, setAptSelectedUsers] = useState<string[]>([]);
  const [aptUserDropdownOpen, setAptUserDropdownOpen] = useState(false);
  const [aptStatusFilter, setAptStatusFilter] = useState<string>('completada');
  const aptUserDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (aptUserDropdownRef.current && !aptUserDropdownRef.current.contains(e.target as Node)) {
        setAptUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const USER_COLORS = ['#14b8a6', '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
  const stats = useMemo(() => {
    const filteredApts = appointments.filter(apt => {
      if (aptDateFrom && apt.date < aptDateFrom) return false;
      if (aptDateTo && apt.date > aptDateTo) return false;
      // Filter by status
      if (aptStatusFilter !== 'todas') {
        if (apt.status !== aptStatusFilter) return false;
      }
      return true;
    });
    const allUsersSet = new Set<string>();
    filteredApts.forEach(apt => allUsersSet.add(apt.createdByUserName || 'Sin registrar'));
    const allAvailableUsers = Array.from(allUsersSet).sort();
    const displayApts = aptSelectedUsers.length > 0 ? filteredApts.filter(apt => aptSelectedUsers.includes(apt.createdByUserName || 'Sin registrar')) : filteredApts;
    const byUser: Record<string, number> = {};
    displayApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      byUser[userName] = (byUser[userName] || 0) + 1;
    });
    const pieData = Object.entries(byUser).map(([name, count], index) => ({
      name,
      value: count,
      color: USER_COLORS[index % USER_COLORS.length]
    })).sort((a, b) => b.value - a.value);
    const getKey = (dateStr: string) => {
      const d = new Date(dateStr + 'T12:00:00');
      if (aptByUserGroupBy === 'day') return dateStr;
      if (aptByUserGroupBy === 'year') return String(d.getFullYear());
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const getLabel = (key: string) => {
      if (aptByUserGroupBy === 'day') {
        const d = new Date(key + 'T12:00:00');
        return d.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short'
        });
      }
      if (aptByUserGroupBy === 'year') return key;
      const [y, m] = key.split('-');
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${monthNames[parseInt(m) - 1]} ${y}`;
    };
    const timeMap: Record<string, Record<string, number>> = {};
    const displayUsers = new Set<string>();
    displayApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      displayUsers.add(userName);
      const key = getKey(apt.date);
      if (!timeMap[key]) timeMap[key] = {};
      timeMap[key][userName] = (timeMap[key][userName] || 0) + 1;
    });
    const timeData = Object.entries(timeMap).sort(([a], [b]) => a.localeCompare(b)).map(([period, users]) => {
      const entry: Record<string, any> = {
        period: getLabel(period)
      };
      displayUsers.forEach(u => {
        entry[u] = users[u] || 0;
      });
      return entry;
    }).slice(-15);
    return {
      pieData,
      timeData,
      userList: Array.from(displayUsers),
      allAvailableUsers,
      totalFiltered: displayApts.length
    };
  }, [appointments, aptByUserGroupBy, aptDateFrom, aptDateTo, aptSelectedUsers, aptStatusFilter]);
  return <div className="space-y-6" data-id="element-2757" data-mp="fl0b7i6uaqvb">
      {/* Filters Card */}
      <Card padding="none" className="overflow-visible relative z-30" data-id="element-2758" data-mp="kgyvatl4xmpx">
        <div className="h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-2759" data-mp="phd44duk90p0" />
        <div className="p-4 md:p-6" data-id="element-2760" data-mp="pfa4d23lvt73">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4" data-id="element-2761" data-mp="thx21jmyk8c7">
            <div className="flex items-center gap-2 min-w-0" data-id="element-2762" data-mp="2qj2s9ixpriiq">
              <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex-shrink-0" data-id="element-2763" data-mp="2qh89ob9x02ht">
                <UserCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" data-id="element-2764" data-mp="2li52oladjm77" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-2765" data-mp="2zdh8potzcz3f">
                Citas Creadas por Usuario
              </h3>
            </div>
            <div className="sm:ml-auto flex items-center gap-2" data-id="element-2766" data-mp="397zecziyj4pv">
              {canDownloadPdf && stats.pieData.length > 0 && <button onClick={() => {
              const cols = [{
                header: 'Usuario',
                key: 'usuario'
              }, {
                header: 'Citas Creadas',
                key: 'citas'
              }];
              const rows = stats.pieData.map(d => ({
                label: d.name,
                values: {
                  usuario: d.name,
                  citas: d.value
                }
              }));
              exportDashboardChartDataToExcel(rows, cols, 'Citas Creadas por Usuario', {
                from: aptDateFrom,
                to: aptDateTo
              });
            }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Descargar Excel" data-id="element-2767" data-mp="34ctsu1tgvs2b">
                
                  <FileSpreadsheetIcon className="w-4 h-4" data-id="element-2768" data-mp="q2ybi6x6w812" />
                </button>}
              {canDownloadPdf && stats.pieData.length > 0 && <button onClick={() => {
              const cols = [{
                header: 'Usuario',
                key: 'usuario'
              }, {
                header: 'Citas Creadas',
                key: 'citas'
              }];
              const rows = stats.pieData.map(d => ({
                label: d.name,
                values: {
                  usuario: d.name,
                  citas: d.value
                }
              }));
              exportDashboardChartDataToPdf(rows, cols, 'Citas Creadas por Usuario', {
                from: aptDateFrom,
                to: aptDateTo
              });
            }} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2769" data-mp="v2b8vllosxhw">
                
                  <DownloadIcon className="w-4 h-4" data-id="element-2770" data-mp="3b7dhgf1vl9kq" />
                </button>}
              <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full" data-id="element-2771" data-mp="369v50eongc7z">
                {stats.totalFiltered} citas
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-id="element-2772" data-mp="2wbfo14jc6jc7">
            <div className="md:col-span-4" data-id="element-2773" data-mp="2savra22odhql">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2774" data-mp="2sbx3wazcacaf">
                Rango de Fecha
              </label>
              <div className="flex items-center gap-2" data-id="element-2775" data-mp="qj8t9gkd8mkm">
                <input type="date" value={aptDateFrom} onChange={e => setAptDateFrom(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-2776" data-mp="28fu3uvrjnlrq" />
                
                <span className="text-gray-400 font-medium" data-id="element-2777" data-mp="2m5xnrg2yb3e0">–</span>
                <input type="date" value={aptDateTo} onChange={e => setAptDateTo(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-2778" data-mp="1phhsd3d5tzsz" />
                
              </div>
            </div>

            <div className="md:col-span-2" data-id="element-2779" data-mp="39x1xijidzu3b">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2780" data-mp="39w0d3ofhdv8x">
                Estado
              </label>
              <Select options={[{
              value: 'completada',
              label: 'Completadas'
            }, {
              value: 'programada',
              label: 'Programadas'
            }, {
              value: 'cancelada',
              label: 'Canceladas'
            }, {
              value: 'reprogramada',
              label: 'Reprogramadas'
            }, {
              value: 'todas',
              label: 'Todas'
            }]} value={aptStatusFilter} onChange={v => setAptStatusFilter(v)} clearable={false} searchable={false} data-id="element-2781" data-mp="2jmphsndajjv6" />
              
            </div>

            <div className="md:col-span-2" data-id="element-2782" data-mp="25orecd0id7ug">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2783" data-mp="2akybpheu9b0s">
                Agrupar Por
              </label>
              <Select options={[{
              value: 'day',
              label: 'Día'
            }, {
              value: 'month',
              label: 'Mes'
            }, {
              value: 'year',
              label: 'Año'
            }]} value={aptByUserGroupBy} onChange={v => setAptByUserGroupBy(v as 'day' | 'month' | 'year')} clearable={false} searchable={false} data-id="element-2784" data-mp="2el9vkrzdn7b7" />
              
            </div>

            <div className="md:col-span-4" ref={aptUserDropdownRef} data-id="element-2785" data-mp="15n4ex6cwqrgv">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2786" data-mp="15l9stopwgtb7">
                Usuarios
              </label>
              <div className="relative" data-id="element-2787" data-mp="vpgb7qxbq9mk">
                <div onClick={() => setAptUserDropdownOpen(!aptUserDropdownOpen)} className={`w-full px-3 py-2 rounded-lg border cursor-pointer bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors duration-150 flex items-center justify-between min-h-[42px] ${aptUserDropdownOpen ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2788" data-mp="vqhv8kwbi6gf">
                  
                  <div className="flex-1 flex flex-wrap gap-1 min-w-0" data-id="element-2789" data-mp="2h06xu82hbt47">
                    {aptSelectedUsers.length === 0 ? <span className="text-gray-400 dark:text-gray-500 text-sm" data-id="element-2790" data-mp="2c13vivdvw0zx">
                        Todos los usuarios
                      </span> : aptSelectedUsers.map(u => <span key={u} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium max-w-[120px]" data-id="element-2791" data-mp="2zzbhso9sslv8">
                      
                          <span className="truncate" data-id="element-2792" data-mp="2r43w5lu80nui">{u}</span>
                          <button onClick={e => {
                      e.stopPropagation();
                      setAptSelectedUsers(prev => prev.filter(x => x !== u));
                    }} className="hover:text-indigo-900 dark:hover:text-indigo-100 flex-shrink-0" type="button" data-id="element-2793" data-mp="3ex3vdavsb06s">
                        
                            <XIcon className="w-3 h-3" data-id="element-2794" data-mp="39v4q1vghht77" />
                          </button>
                        </span>)}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2" data-id="element-2795" data-mp="1yhj4sldqwbqy">
                    {aptSelectedUsers.length > 0 && <button onClick={e => {
                    e.stopPropagation();
                    setAptSelectedUsers([]);
                  }} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" type="button" data-id="element-2796" data-mp="1qxrrrag8ywjy">
                      
                        <XIcon className="w-4 h-4 text-gray-400" data-id="element-2797" data-mp="1zxwuh7ahx6rs" />
                      </button>}
                    <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform ${aptUserDropdownOpen ? 'rotate-90' : ''}`} data-id="element-2798" data-mp="1v0ejzcgtyjho" />
                    
                  </div>
                </div>

                {aptUserDropdownOpen && <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-2799" data-mp="1gzu0ee33cjn4">
                    {stats.allAvailableUsers.length === 0 ? <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center" data-id="element-2800" data-mp="oj16gupylsyk">
                        No hay usuarios en este rango
                      </div> : <>
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex gap-2" data-id="element-2801" data-mp="x7f8mc24609j">
                          <button onClick={() => setAptSelectedUsers([...stats.allAvailableUsers])} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium" type="button" data-id="element-2802" data-mp="125qi2lohy8r8">
                        
                            Seleccionar todos
                          </button>
                          <span className="text-gray-300 dark:text-gray-600" data-id="element-2803" data-mp="1fyg2wmsah7mr">
                            |
                          </span>
                          <button onClick={() => setAptSelectedUsers([])} className="text-xs text-gray-500 dark:text-gray-400 hover:underline font-medium" type="button" data-id="element-2804" data-mp="1kuwu5pusc242">
                        
                            Limpiar
                          </button>
                        </div>
                        {stats.allAvailableUsers.map(userName => {
                    const isSelected = aptSelectedUsers.includes(userName);
                    return <div key={userName} onClick={() => setAptSelectedUsers(prev => isSelected ? prev.filter(x => x !== userName) : [...prev, userName])} className={`px-3 py-2.5 cursor-pointer flex items-center gap-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors ${isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`} data-id="element-2805" data-mp="2dqxpcjjdzv3c">
                          
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2806" data-mp="1zxy8ad3yhsto">
                            
                                {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-2807" data-mp="2nnitl2nl1264" />}
                              </div>
                              <div className="flex items-center gap-2 min-w-0" data-id="element-2808" data-mp="2isd7o3u6jni1">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" data-id="element-2809" data-mp="1cbrnsrdcikmk">
                                  {userName.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-900 dark:text-gray-100 truncate" data-id="element-2810" data-mp="1dqkn7zuteivc">
                                  {userName}
                                </span>
                              </div>
                            </div>;
                  })}
                      </>}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2811" data-mp="uuchkuxw45zi">
        <Card padding="none" className="overflow-hidden lg:col-span-2" data-id="element-2812" data-mp="h2ebyu3e0s83">
          <div className="h-1 bg-gradient-to-r from-indigo-500 to-violet-500" data-id="element-2813" data-mp="l1wytverrddk" />
          <div className="p-4 md:p-6" data-id="element-2814" data-mp="7371ceo1fboo">
            <CardHeader className="px-0 pt-0" data-id="element-2815" data-mp="748ecoom6891">
              <CardTitle className="flex items-center gap-2 text-base" data-id="element-2816" data-mp="c20gebiz3aqy">
                <BarChart3Icon className="w-5 h-5 text-indigo-500" data-id="element-2817" data-mp="c18iuv5tf2y1" />
                Citas por Período
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2818" data-mp="3jn1fdzr0b2ka">
              {stats.timeData.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2819" data-mp="2ee9uvrffdc6">
                  <UserCheckIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2820" data-mp="3tj4esxuh4n3j" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2821" data-mp="bdhtq1yf3wrq">
                    No hay datos en este rango
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2822" data-mp="l4m8p1o3fhmk">
                    Ajusta las fechas o los usuarios seleccionados
                  </p>
                </div> : <ResponsiveContainer width="100%" height={320} data-id="element-2823" data-mp="g85d5meke0zg">
                  <BarChart data={stats.timeData} data-id="element-2824" data-mp="u63oeixl9haq">
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} vertical={false} data-id="element-2825" data-mp="q48qloev9m1p" />
                  
                    <XAxis dataKey="period" stroke={chartAxisColor} fontSize={11} tickLine={false} axisLine={false} angle={aptByUserGroupBy === 'day' ? -45 : 0} textAnchor={aptByUserGroupBy === 'day' ? 'end' : 'middle'} height={aptByUserGroupBy === 'day' ? 60 : 30} data-id="element-2826" data-mp="1wjpvuud8bntf" />
                  
                    <YAxis stroke={chartAxisColor} fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-2827" data-mp="2aj7s1usuu3ct" />
                  
                    <Tooltip contentStyle={{
                  backgroundColor: chartTooltipBg,
                  border: chartTooltipBorder,
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  padding: '12px 16px',
                  color: chartTooltipColor
                }} formatter={(value: number, name: string) => [<span style={{
                  fontWeight: 'bold'
                }} data-id="element-2829" data-mp="v2qsc07ishaz">{value}</span>, name]} data-id="element-2828" data-mp="1du064lobps8d" />
                  
                    <Legend wrapperStyle={{
                  paddingTop: '16px'
                }} data-id="element-2830" data-mp="z1pvbf9oy3zs" />
                  
                    {stats.userList.map((userName, index) => <Bar key={userName} dataKey={userName} name={userName} fill={USER_COLORS[index % USER_COLORS.length]} radius={[4, 4, 0, 0]} stackId="users" data-id="element-2831" data-mp="mkotmqwmoh60" data-mpi={index} />)}
                  </BarChart>
                </ResponsiveContainer>}
            </CardContent>
          </div>
        </Card>

        <Card padding="none" className="overflow-hidden" data-id="element-2832" data-mp="8kvy8ia5pcrp">
          <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500" data-id="element-2833" data-mp="8mgrm4kqipnz" />
          <div className="p-4 md:p-6" data-id="element-2834" data-mp="3tnotm8ttrej">
            <CardHeader className="px-0 pt-0" data-id="element-2835" data-mp="3smcleg2qyv8">
              <CardTitle className="flex items-center gap-2 text-base" data-id="element-2836" data-mp="3v1ry3243lkfz">
                <UsersIcon className="w-5 h-5 text-violet-500" data-id="element-2837" data-mp="3qzon0elcchv8" />
                Total por Usuario
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2838" data-mp="3lzae6ubx13w5">
              {stats.pieData.length === 0 ? <div className="text-center py-8 text-gray-400" data-id="element-2839" data-mp="3h2a6yg3bxv3g">Sin datos</div> : <>
                  <ResponsiveContainer width="100%" height={200} data-id="element-2840" data-mp="3fsvwmyqcp952">
                    <PieChart data-id="element-2841" data-mp="3atjbqjj1ic54">
                      <Pie data={stats.pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" data-id="element-2842" data-mp="3at9j0o6m1qvq">
                      
                        {stats.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2843" data-mp="30w4vol113pdc" data-mpi={index} />)}
                      </Pie>
                      <Tooltip contentStyle={{
                    backgroundColor: chartTooltipBg,
                    border: chartTooltipBorder,
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    padding: '8px 12px',
                    color: chartTooltipColor
                  }} formatter={(value: number, name: string) => [<span style={{
                    fontWeight: 'bold'
                  }} data-id="element-2845" data-mp="2d6alycc0agxb">{value} citas</span>, name]} data-id="element-2844" data-mp="2wu09g7cwtv0q" />
                    
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-4" data-id="element-2846" data-mp="omwqu8bl3ezo">
                    {stats.pieData.map((item, index) => <div key={index} className="flex items-center justify-between text-sm" data-id="element-2847" data-mp="sn72a0i5axlx" data-mpi={index}>
                    
                        <div className="flex items-center gap-2" data-id="element-2848" data-mp="xnuwev1o7ukd">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
                      backgroundColor: item.color
                    }} data-id="element-2849" data-mp="12lo09hfi4chh" />
                      
                          <span className="text-gray-700 dark:text-gray-300 truncate max-w-[140px]" data-id="element-2850" data-mp="4wb84wq7vqbi">
                            {item.name}
                          </span>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white" data-id="element-2851" data-mp="jn1w1toou59c">
                          {item.value}
                        </span>
                      </div>)}
                  </div>
                </>}
            </CardContent>
          </div>
        </Card>
      </div>
    </div>;
}